import React, { useState } from 'react';
import { Info, AlertTriangle, CheckCircle2, RotateCcw, Hand } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · M3 · Volant + positionnement sur la chaussée (REMC C1c)
// Œuvre pédagogique interactive :
//  · SECTION 1 : « TIRER-POUSSER » — mini-animation d'un volant avec deux mains
//    à 9h15, angle réglable par slider. Les mains restent SUR le volant sans
//    jamais se croiser (démonstration de la technique).
//  · SECTION 2 : POSITIONNEMENT — vue de dessus d'une route française à 2 voies.
//    La voiture EGO reste TOUJOURS sur la VOIE DE DROITE et se déplace
//    latéralement entre le trottoir (bord droit) et la ligne médiane. Animation
//    fluide entre 3 positions préréglées + slider libre pour explorer.
// ══════════════════════════════════════════════════════════════════════════

// ── Repères géométriques (coordonnées LOCALES du panneau route) ──────────
//   Panneau route à translate(420,40), viewBox interne 0..350
//   Trottoir droit (bord droit chaussée) : local x = 60→65 (blanche)
//   VOIE DE DROITE (où l'on roule)        : local x = 65 → 175  (largeur 110 px)
//   Ligne médiane (pointillé jaune)       : local x = 175
//   Voie de gauche (trafic inverse)       : local x = 175 → 285
//   1 px  ≈ 3.5 m / 110 px = 0.0318 m
const CAR_HALF_W = 18;           // demi-largeur de la voiture en px
const RIGHT_LANE_LEFT = 65;      // bord droit chaussée (trottoir)
const RIGHT_LANE_RIGHT = 175;    // ligne médiane
const PX_TO_M = 3.5 / (RIGHT_LANE_RIGHT - RIGHT_LANE_LEFT);

// Convertit une position locale (px) en jauges de distance réelles (m)
function distancesFor(localX) {
  const bord = ((localX - CAR_HALF_W) - RIGHT_LANE_LEFT) * PX_TO_M;
  const med  = (RIGHT_LANE_RIGHT - (localX + CAR_HALF_W)) * PX_TO_M;
  return { bord: Math.max(0, bord), med: Math.max(0, med) };
}

const POSITIONS = [
  {
    id: 'parfaite',
    label: 'Position parfaite',
    color: '#22c55e',
    x: 120, // CENTRE de la voie de droite (65 + 110/2)
    fonction: "Voiture centrée dans sa voie de droite, à égale distance du trottoir et de la ligne médiane.",
    geste: "Vise le centre de ta voie. Le côté droit du capot doit aligner avec le milieu de la voie devant toi. Regard porté loin (100-200 m).",
    piege: "Regarder la route juste devant le capot → tu \"chasses le volant\", trajectoire zigzagante.",
  },
  {
    id: 'centre',
    label: 'Trop à gauche (vers la médiane)',
    color: '#f59e0b',
    x: 152, // proche de la médiane, TOUJOURS dans la voie de droite
    fonction: "Voiture décalée à gauche DANS SA VOIE, trop proche de la ligne médiane. Sur les routes étroites tu déborderais sur la voie inverse.",
    geste: "Décale-toi de 10-20 cm à droite. Objectif : conserver 1 m minimum de la ligne médiane.",
    piege: "Un véhicule qui déborde en face (camion large, cycliste inverse) → collision quasi-inévitable.",
  },
  {
    id: 'bord',
    label: 'Trop à droite (vers le bord)',
    color: '#dc2626',
    x: 88, // proche du trottoir, toujours dans la voie de droite
    fonction: "Voiture décalée à droite DANS SA VOIE, collée au trottoir/accotement. Zone dangereuse : piétons, portières, nids-de-poule, marquage abîmé.",
    geste: "Décale-toi vers le centre (10-20 cm). Objectif : conserver 1 m minimum du bord droit.",
    piege: "Un piéton qui ouvre sa portière + un enfant qui traverse = double collision. Roue qui accroche l'accotement = perte de contrôle.",
  },
];

export default function VolantChausseeSvg() {
  const [pos, setPos] = useState('parfaite');
  const [angle, setAngle] = useState(0); // Angle du volant en degrés (-180 à 180)
  // laneX = position locale x réelle de la voiture. null = pilotée par le preset.
  const [laneX, setLaneX] = useState(null);
  const preset = POSITIONS.find((p) => p.id === pos);
  const currentX = laneX !== null ? laneX : preset.x;
  const { bord: distBord, med: distMedian } = distancesFor(currentX);

  // Détermine dynamiquement le label / couleur en fonction de la position réelle
  const status = (() => {
    if (distBord < 0.8) return POSITIONS.find((p) => p.id === 'bord');
    if (distMedian < 0.8) return POSITIONS.find((p) => p.id === 'centre');
    if (distBord >= 1.0 && distBord <= 1.5 && distMedian >= 1.0 && distMedian <= 1.5) return POSITIONS.find((p) => p.id === 'parfaite');
    // Zone acceptable mais pas parfaite → couleur ambre
    return {
      ...POSITIONS.find((p) => p.id === 'parfaite'),
      color: distBord < 1 || distMedian < 1 ? '#f59e0b' : '#22c55e',
      label: distBord < distMedian ? 'Un peu à droite' : (distMedian < distBord ? 'Un peu à gauche' : 'Position parfaite'),
    };
  })();
  const current = laneX !== null ? status : preset;

  const setPreset = (id) => { setPos(id); setLaneX(null); };
  const reset = () => { setPos('parfaite'); setAngle(0); setLaneX(null); };

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-white/15 overflow-hidden">
          {POSITIONS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPreset(p.id)}
              data-testid={`m3-position-${p.id}`}
              className={`h-9 px-3 text-[11px] font-bold font-mono-spec inline-flex items-center gap-1.5 transition ${
                pos === p.id ? 'text-white' : 'text-white/60 hover:bg-white/5'
              } ${pos === p.id ? '' : 'border-l first:border-l-0 border-white/15'}`}
              style={pos === p.id ? { background: p.color + '25', borderLeft: p.id === POSITIONS[0].id ? 'none' : `1px solid ${p.color}` } : {}}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={reset}
          data-testid="m3-reset"
          className="ml-auto h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[11.5px] font-semibold inline-flex items-center gap-1"
        >
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Volant + positionnement chaussée">
            <defs>
              <linearGradient id="m3-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#334155" />
                <stop offset="100%" stopColor="#1e293b" />
              </linearGradient>
              <linearGradient id="m3-wheel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f3f46" />
                <stop offset="100%" stopColor="#18181b" />
              </linearGradient>
              <linearGradient id="m3-car-body" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#dc2626" />
                <stop offset="100%" stopColor="#7f1d1d" />
              </linearGradient>
            </defs>

            {/* ═════════ SECTION VOLANT (gauche, 350×500) ═════════ */}
            <g transform="translate(30 40)">
              <rect width={350} height={520} rx={16} fill="#0f172a" stroke="#334155" strokeWidth={1.5} />
              <text x={175} y={26} textAnchor="middle" fill="#94a3b8" fontSize={12} fontFamily="monospace" fontWeight="bold">
                TECHNIQUE DU VOLANT · TIRER-POUSSER
              </text>
              <text x={175} y={40} textAnchor="middle" fill="#64748b" fontSize={10}>Slider = angle de braquage · les mains ne se croisent JAMAIS</text>

              {/* Volant central (transformé par angle) */}
              <g transform={`translate(175 240) rotate(${angle})`}>
                <ellipse cx={0} cy={0} rx={110} ry={110} fill="none" stroke="url(#m3-wheel)" strokeWidth={20} />
                <ellipse cx={0} cy={0} rx={110} ry={110} fill="none" stroke="#71717a" strokeWidth={2} />
                {/* Moyeu */}
                <circle r={32} fill="#18181b" stroke="#71717a" strokeWidth={2} />
                <text y={5} textAnchor="middle" fill="#71717a" fontSize={14} fontWeight="900" fontFamily="serif">CV</text>
                {/* Rayons */}
                <line x1={-32} y1={0} x2={-110} y2={0} stroke="#3f3f46" strokeWidth={6} />
                <line x1={32} y1={0} x2={110} y2={0} stroke="#3f3f46" strokeWidth={6} />
                <line x1={0} y1={32} x2={0} y2={110} stroke="#3f3f46" strokeWidth={6} />

                {/* Main GAUCHE à 9h (position 180° du repère volant) */}
                <g transform="translate(-110 0)">
                  <ellipse cx={0} cy={0} rx={16} ry={22} fill="#f5c9a2" stroke="#0b1120" strokeWidth={1.5} />
                  <rect x={-8} y={-2} width={16} height={20} rx={4} fill="#f5c9a2" stroke="#0b1120" strokeWidth={1.5} />
                  <text y={4} textAnchor="middle" fill="#0b1120" fontSize={10} fontWeight="900" transform={`rotate(${-angle})`}>G</text>
                </g>
                {/* Main DROITE à 3h */}
                <g transform="translate(110 0)">
                  <ellipse cx={0} cy={0} rx={16} ry={22} fill="#f5c9a2" stroke="#0b1120" strokeWidth={1.5} />
                  <rect x={-8} y={-2} width={16} height={20} rx={4} fill="#f5c9a2" stroke="#0b1120" strokeWidth={1.5} />
                  <text y={4} textAnchor="middle" fill="#0b1120" fontSize={10} fontWeight="900" transform={`rotate(${-angle})`}>D</text>
                </g>
              </g>

              {/* Indicateur d'angle */}
              <g transform="translate(175 380)">
                <text y={0} textAnchor="middle" fill="#94a3b8" fontSize={10} fontFamily="monospace" fontWeight="bold">ANGLE DE BRAQUAGE</text>
                <text y={26} textAnchor="middle" fill={Math.abs(angle) > 90 ? '#f59e0b' : '#22c55e'} fontSize={22} fontWeight="900" fontFamily="monospace">
                  {angle > 0 ? '+' : ''}{angle}°
                </text>
                <text y={44} textAnchor="middle" fill="#64748b" fontSize={9}>
                  {angle === 0 ? '↑ Droit devant' : angle < 0 ? '← Braquage à gauche' : 'Braquage à droite →'}
                </text>
              </g>

              {/* Direction de la roue avant */}
              <g transform={`translate(175 460) rotate(${angle * 0.3})`}>
                <rect x={-3} y={-20} width={6} height={40} rx={2} fill="#22c55e" />
                <polygon points="0,-30 -6,-16 6,-16" fill="#22c55e" />
              </g>

              {/* Repères horloge */}
              {[0, 90, 180, 270].map((ang) => {
                const rad = (ang - 90) * Math.PI / 180;
                const rx = 175 + 130 * Math.cos(rad);
                const ry = 240 + 130 * Math.sin(rad);
                const label = ang === 0 ? '12h' : ang === 90 ? '3h' : ang === 180 ? '6h' : '9h';
                return (
                  <text key={ang} x={rx} y={ry} textAnchor="middle" fill="#475569" fontSize={9} fontFamily="monospace" alignmentBaseline="middle">{label}</text>
                );
              })}
            </g>

            {/* ═════════ SECTION POSITIONNEMENT (droite, 350×520) ═════════ */}
            <g transform="translate(420 40)">
              <rect width={350} height={520} rx={16} fill="#0f172a" stroke="#334155" strokeWidth={1.5} />
              <text x={175} y={26} textAnchor="middle" fill="#94a3b8" fontSize={12} fontFamily="monospace" fontWeight="bold">
                POSITIONNEMENT SUR LA CHAUSSÉE
              </text>
              <text x={175} y={40} textAnchor="middle" fill="#64748b" fontSize={10}>vue de dessus · 2 voies · France</text>

              {/* Bord droit (trottoir) */}
              <rect x={20} y={60} width={40} height={440} fill="#475569" />
              <rect x={60} y={60} width={5} height={440} fill="#94a3b8" />
              {/* Bord gauche (autre bord) */}
              <rect x={290} y={60} width={40} height={440} fill="#475569" />
              <rect x={285} y={60} width={5} height={440} fill="#94a3b8" />
              {/* Chaussée */}
              <rect x={65} y={60} width={220} height={440} fill="url(#m3-road)" />
              {/* Ligne médiane pointillée */}
              <line x1={175} y1={60} x2={175} y2={500} stroke="#fbbf24" strokeWidth={3} strokeDasharray="20 14" opacity={0.8} />

              {/* Zone de sécurité idéale (rectangle vert clair au centre de la voie de droite) */}
              <rect x={90} y={60} width={60} height={440} fill="#22c55e" opacity={0.10} />
              <line x1={120} y1={60} x2={120} y2={500} stroke="#22c55e" strokeDasharray="2 6" strokeWidth={1} opacity={0.4} />

              {/* Label VOIE DE DROITE (bandeau haut de la voie où roule la voiture) */}
              <g transform="translate(120 55)">
                <rect x={-45} y={-14} width={90} height={16} rx={8} fill="#22c55e" opacity={0.85} />
                <text y={-2} textAnchor="middle" fill="#052e16" fontSize={9} fontFamily="monospace" fontWeight="900">VOIE DE DROITE</text>
              </g>

              {/* Labels bords */}
              <text x={40} y={480} fill="#94a3b8" fontSize={9} fontFamily="monospace" transform="rotate(-90 40 480)">TROTTOIR / BORD DROIT</text>
              <text x={175} y={55} fill="#fbbf24" fontSize={9} fontFamily="monospace" textAnchor="middle">← LIGNE MÉDIANE →</text>
              <text x={310} y={490} fill="#94a3b8" fontSize={9} fontFamily="monospace" transform="rotate(-90 310 490)">VOIE INVERSE</text>

              {/* Voiture EGO (colorée selon la qualité de position) */}
              {/* currentX = coord locale x DANS le panneau route (translate 420,40) */}
              <g style={{ transform: `translate(${currentX}px, 280px)`, transition: 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <ellipse cx={0} cy={4} rx={22} ry={5} fill="#000" opacity={0.4} />
                <rect x={-CAR_HALF_W} y={-40} width={CAR_HALF_W * 2} height={80} rx={12} fill={`${current.color}`} stroke="#0b1120" strokeWidth={2.5} />
                <path d="M -14,-35 L 14,-35 L 12,-14 L -12,-14 Z" fill="#0ea5e9" opacity={0.55} />
                <path d="M -14,14 L 14,14 L 12,32 L -12,32 Z" fill="#0ea5e9" opacity={0.45} />
                <rect x={-12} y={-14} width={24} height={28} fill="#000" opacity={0.25} />
                <circle cx={-10} cy={-33} r={3.5} fill="#fef3c7" />
                <circle cx={10} cy={-33} r={3.5} fill="#fef3c7" />
                <circle r={11} fill="#fff" stroke={current.color} strokeWidth={2} />
                <text y={4} textAnchor="middle" fontSize={9} fontWeight="900" fill={current.color}>MOI</text>
                {/* Flèche direction (avance) */}
                <polygon points="0,-52 -5,-42 5,-42" fill="#22c55e" opacity={0.9} />
              </g>

              {/* Jauges de distance calculées dynamiquement */}
              {/* Jauge bord droit : de x=65 (trottoir) à x=currentX-CAR_HALF_W */}
              {(() => {
                const bordColor = distBord < 1 ? '#dc2626' : (distBord <= 1.5 ? '#22c55e' : '#f59e0b');
                const medColor  = distMedian < 1 ? '#dc2626' : (distMedian <= 1.5 ? '#22c55e' : '#f59e0b');
                return (
                  <g>
                    <g style={{ transform: 'translate(65px, 260px)' }}>
                      <line x1={0} y1={0} x2={currentX - CAR_HALF_W - RIGHT_LANE_LEFT} y2={0} stroke={bordColor} strokeWidth={2} strokeDasharray="4 3" style={{ transition: 'all 700ms' }} />
                      <line x1={0} y1={-6} x2={0} y2={6} stroke={bordColor} strokeWidth={2} />
                      <line x1={currentX - CAR_HALF_W - RIGHT_LANE_LEFT} y1={-6} x2={currentX - CAR_HALF_W - RIGHT_LANE_LEFT} y2={6} stroke={bordColor} strokeWidth={2} style={{ transition: 'all 700ms' }} />
                      <text x={(currentX - CAR_HALF_W - RIGHT_LANE_LEFT) / 2} y={-10} textAnchor="middle" fill={bordColor} fontSize={10.5} fontWeight="bold" fontFamily="monospace" style={{ transition: 'all 700ms' }}>
                        {distBord.toFixed(2)} m
                      </text>
                    </g>
                    <g style={{ transform: 'translate(175px, 320px)' }}>
                      <line x1={currentX + CAR_HALF_W - RIGHT_LANE_RIGHT} y1={0} x2={0} y2={0} stroke={medColor} strokeWidth={2} strokeDasharray="4 3" style={{ transition: 'all 700ms' }} />
                      <line x1={currentX + CAR_HALF_W - RIGHT_LANE_RIGHT} y1={-6} x2={currentX + CAR_HALF_W - RIGHT_LANE_RIGHT} y2={6} stroke={medColor} strokeWidth={2} style={{ transition: 'all 700ms' }} />
                      <line x1={0} y1={-6} x2={0} y2={6} stroke={medColor} strokeWidth={2} />
                      <text x={(currentX + CAR_HALF_W - RIGHT_LANE_RIGHT) / 2} y={-10} textAnchor="middle" fill={medColor} fontSize={10.5} fontWeight="bold" fontFamily="monospace" style={{ transition: 'all 700ms' }}>
                        {distMedian.toFixed(2)} m
                      </text>
                    </g>
                  </g>
                );
              })()}

              {/* Encadré valeur cible */}
              <g transform="translate(20 470)">
                <rect width={310} height={30} rx={6} fill={current.color + '20'} stroke={current.color} strokeWidth={1.5} />
                <text x={155} y={13} textAnchor="middle" fill={current.color} fontSize={10} fontFamily="monospace" fontWeight="bold">CIBLE : 1 m ≤ distance ≤ 1,40 m</text>
                <text x={155} y={25} textAnchor="middle" fill="#94a3b8" fontSize={9}>
                  bord droit + médiane doivent tous les deux être dans cette zone
                </text>
              </g>
            </g>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.65} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">M3 · Volant + positionnement sur la chaussée</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C1c — Trajectoire et positionnement</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>

          {/* Slider angle volant */}
          <div className="p-4 border-t border-white/10 bg-black/40 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="m3-lane" className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
                  🛣 Position latérale dans la voie de droite
                </label>
                <span className="font-mono-spec text-white text-[13px] font-bold">
                  bord {distBord.toFixed(2)}<span className="text-white/40 text-[10px]"> m</span>
                  <span className="text-white/40 mx-1">·</span>
                  méd. {distMedian.toFixed(2)}<span className="text-white/40 text-[10px]"> m</span>
                </span>
              </div>
              <input
                id="m3-lane"
                type="range"
                min={RIGHT_LANE_LEFT + CAR_HALF_W}
                max={RIGHT_LANE_RIGHT - CAR_HALF_W}
                step={1}
                value={currentX}
                onChange={(e) => setLaneX(Number(e.target.value))}
                data-testid="m3-lane-slider"
                className="w-full accent-emerald-500 h-2"
                aria-label="Position latérale dans la voie"
              />
              <div className="mt-1 flex justify-between text-[10px] text-white/30 font-mono-spec">
                <span>← Trottoir (bord droit)</span>
                <span>Centre voie</span>
                <span>Médiane →</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="m3-angle" className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
                  <Hand size={12} /> Angle de braquage du volant
                </label>
                <span className="font-mono-spec text-white text-[13px] font-bold">
                  {angle > 0 ? '+' : ''}{angle}<span className="text-white/40 text-[10px]">°</span>
                </span>
              </div>
              <input
                id="m3-angle"
                type="range"
                min={-180}
                max={180}
                step={5}
                value={angle}
                onChange={(e) => setAngle(Number(e.target.value))}
                data-testid="m3-angle-slider"
                className="w-full accent-cyan-500 h-2"
                aria-label="Angle du volant"
              />
              <div className="mt-1 flex justify-between text-[10px] text-white/30 font-mono-spec">
                <span>-180° (½ tour G)</span>
                <span>0° (droit)</span>
                <span>+180° (½ tour D)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="m3-panel">
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: current.color, background: current.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: current.color }}>
              Position actuelle
            </div>
            <div className="mt-1 font-serif-editorial text-white text-[20px] leading-tight">
              {current.label}
            </div>
            <div className="mt-1.5 flex items-center gap-3 text-[11px]">
              <span className="text-white/60 font-mono-spec">Bord droit : <b className="text-white">{distBord.toFixed(2)} m</b></span>
              <span className="text-white/60 font-mono-spec">Médiane : <b className="text-white">{distMedian.toFixed(2)} m</b></span>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Description</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.fonction}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Le bon geste</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.geste}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Piège classique</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.piege}</p>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-purple-500/40 bg-purple-950/20 p-3">
            <div className="text-purple-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold flex items-center gap-1.5">
              <Hand size={12} /> Technique tirer-pousser
            </div>
            <ul className="mt-1.5 space-y-1 text-white/85 text-[12px] leading-relaxed list-disc ml-4">
              <li>Mains à <b>9h15</b> (main gauche à 9h, droite à 3h)</li>
              <li>La main qui <b>TIRE</b> descend en tournant, l&apos;autre pousse en glissant</li>
              <li>Les mains ne <b>SE CROISENT JAMAIS</b> — sinon perte de contrôle en cas de dérapage</li>
              <li>Retour au neutre : laisse le volant « rentrer » par lui-même, freine ta main dessus</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
